import { useCallback } from 'react';
import { buildPreviewModel } from '../../lib/briefModel.js';
import BriefArticleSidebar from './BriefArticleSidebar.jsx';
import BriefCategoryNav from './BriefCategoryNav.jsx';
import BriefDailySummary from './BriefDailySummary.jsx';
import BriefLead from './BriefLead.jsx';
import BriefMarketPulse from './BriefMarketPulse.jsx';
import BriefPillarSection from './BriefPillarSection.jsx';
import { scrollToBriefTarget } from './briefScroll.js';
import { BRIEF_SHELL } from './briefWebStyles.js';
import { useBriefHashScroll } from './useBriefHashScroll.js';
import { useBriefScrollSpy } from './useBriefScrollSpy.js';

/**
 * Subscriber web brief (Newsletter v2 layout). Content from briefModel.js;
 * visual design lives only in src/components/brief/* — not the email template.
 */
export default function WebBrief({ edition, briefPath = '/', ready = false }) {
  useBriefHashScroll(ready);

  if (!edition) return null;

  const siteBaseUrl =
    typeof window !== 'undefined' ? window.location.origin : 'https://hangarxdaily.com';
  const model = buildPreviewModel(edition, { siteBaseUrl, briefPath });

  const { activeArticleId, scrollToArticle } = useBriefScrollSpy(ready, model.articles);

  const handleNavigate = useCallback(
    (target) => {
      if (target === '__summary__') {
        scrollToBriefTarget('#daily-summary');
        return;
      }
      scrollToArticle(target);
    },
    [scrollToArticle]
  );

  const hasArticles = model.articles.length > 0;

  const briefFooter = (
    <footer className="text-center border-t-4 border-black pt-7 pb-4 mt-8">
      <div className="font-sans font-black text-[16px] tracking-[0.3em] text-black mb-3">
        HANGAR X · DAILY
      </div>
      <p className="font-sans text-[14px] text-[#525252]">
        Get the full briefing every morning at{' '}
        <a
          href={model.onlineUrl}
          className="text-[#C8351A] font-semibold border-b-2 border-[#C8351A] no-underline pb-0.5 hover:text-black hover:border-black"
        >
          hangarxdaily.com
        </a>
      </p>
    </footer>
  );

  const briefMain = (
    <>
      <BriefLead model={model} />
      <BriefDailySummary model={model} />
      <BriefMarketPulse marketPulse={model.marketPulse} />
      {hasArticles && (
        <>
          <BriefArticleSidebar
            variant="mobile"
            model={model}
            activeArticleId={activeArticleId}
            onArticleSelect={handleNavigate}
          />
          <BriefCategoryNav sections={model.sections} className="lg:hidden" />
          {model.sections.map((section, i) => (
            <BriefPillarSection
              key={section.category}
              section={section}
              isFirstSection={i === 0}
            />
          ))}
        </>
      )}
      {briefFooter}
    </>
  );

  return (
    <div className={BRIEF_SHELL}>
      {hasArticles ? (
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(240px,268px)] lg:gap-7 xl:gap-9 lg:items-start">
          <div className="min-w-0">{briefMain}</div>
          <BriefArticleSidebar
            variant="desktop"
            model={model}
            activeArticleId={activeArticleId}
            onArticleSelect={handleNavigate}
          />
        </div>
      ) : (
        briefMain
      )}
    </div>
  );
}
